import React, { useEffect, useState, useMemo } from "react";
import moment from "moment";
import { LockedPage, LockedPageModal } from "../index";
import { useAlert, useArchSnapshotSummaryClient, useArchSnapshotGenerateClient, useArchSnapshotConfirmAccessClient, useFormValidation, useFormValues, useSharedStorage } from "../../../index";
import { Button, ControlledValidationCheckbox, ControlledValidationSelect, InlineLoader, FormValidation, PageLoader, Widget, TableSortWrapper, TableBodyNoDataWrapper, Table, TableHeader, TableBody, Link, ReactRouterLink, Section } from "../../../styled";
import archSnapshotLockedBackground from "./archSnapshotLockedBackground.png";
import cloudFormationLaunchStackImage from "./cloudFormationLaunchStack.png";
import "./archSnapshots.css";
moment.suppressDeprecationWarnings = true;
const ArchSnapshot = () => {
  // shared storage
  const [route_cloud_account_setup] = useSharedStorage("route_cloud_account_setup");
  const [cloudType] = useSharedStorage("cac_in_focus_cloud_type");

  // request client
  const [getArchSnapshotSummary, archSnapshotSummary] = useArchSnapshotSummaryClient();
  const [postArchSnapshotGenerate] = useArchSnapshotGenerateClient();
  const [postArchSnapshotConfirmAccess] = useArchSnapshotConfirmAccessClient();

  // ui hooks
  const [Alert, createAlert, removeAlert] = useAlert();
  const [createSnapshotFormValues, onCreateSnapshotFieldChange, resetCreateSnapshotFormValues] = useFormValues({
    regions: []
  });

  // component state
  const [snapshots, setSnapshots] = useState([]);
  const [isSnapshotGenerationInProgress, setIsSnapshotGenerationInProgress] = useState(false);
  const [isSnapshotGenerationInitInProgress, setIsSnapshotGenerationInitInProgress] = useState(false);
  const [isSnapshotGenerationInitButtonDisabled, setIsSnapshotGenerationInitButtonDisabled] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [regionList, setRegionList] = useState([]);
  const [recentlyUsedRegionList, setRecentlyUsedRegionList] = useState([]);
  const [isTemplateCreated, setIsTemplateCreated] = useState(true);
  const [isTemplateConfirmLoading, setIsTemplateConfirmLoading] = useState(false);
  let memoizedSnapshotRows = useMemo(() => snapshots, [snapshots]);
  let memoizedSnapshotCols = useMemo(() => {
    let cols = [{
      dataKey: "snapshot_id",
      label: "View Snapshot",
      columnStyles: {
        minWidth: "180px",
        width: "20%"
      },
      renderCell: (cell, row) => {
        return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("a", {
          href: row.image_url,
          target: "_blank"
        }, /*#__PURE__*/React.createElement(Button, {
          size: "small",
          styleType: "primary",
          disabled: row.generation_state !== "complete"
        }, "View")));
      }
    }, {
      dataKey: "scope_value",
      label: "Region",
      sortEnabled: true,
      columnStyles: {
        minWidth: "150px",
        width: "40%"
      }
    }, {
      dataKey: "generation_state",
      label: "Generation Status",
      sortEnabled: true,
      columnStyles: {
        minWidth: "150px",
        width: "20%"
      },
      renderCell: (cell, row) => {
        if (cell === "in_progress") {
          return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(InlineLoader, {
            colorType: "primary"
          }), "In Progress");
        }
        if (cell === "complete") {
          return /*#__PURE__*/React.createElement("div", {
            className: "arch-snapshot-generation-state-complete"
          }, "Complete");
        }
        if (cell === "failed") {
          return /*#__PURE__*/React.createElement("div", {
            className: "arch-snapshot-generation-state-failed"
          }, "Failed");
        }
        return /*#__PURE__*/React.createElement("div", null, cell);
      }
    }, {
      dataKey: "generation_state_dt",
      label: "Generation Status Date",
      sortEnabled: true,
      columnStyles: {
        minWidth: "150px",
        width: "20%"
      },
      alignContent: "right",
      renderCell: (cell, row) => {
        return handleLocalDatetime(cell, "date-time-2");
      }
    }];
    return cols;
  }, [snapshots]);
  useEffect(() => {
    if (!cloudType) return;
    if (cloudType === "aws") {
      handleGetArchSnapshotSummary();
      return;
    }
    setPageLoading(false);
  }, [cloudType]);
  useEffect(() => {
    let interval;
    if (isSnapshotGenerationInProgress) {
      interval = setInterval(() => {
        handleGetArchSnapshotSummary();
      }, 10000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isSnapshotGenerationInProgress]);
  const handleGetArchSnapshotSummary = async () => {
    removeAlert();
    const newArchSnapshotSummary = await getArchSnapshotSummary();
    if (newArchSnapshotSummary?.is_error) {
      setPageLoading(false);
      createAlert("error", "Error while gathering snapshot information. Please try again soon");
      return;
    }
    if (newArchSnapshotSummary.snapshot_access.template.template_version_in_use !== newArchSnapshotSummary.snapshot_access.template.template_version_latest) {
      setIsTemplateCreated(false);
    }
    setRegionList(newArchSnapshotSummary.scope_meta);
    const newRecentlyUsedRegionList = newArchSnapshotSummary.scope_meta.filter(scope => scope.is_recently_used === true);
    setRecentlyUsedRegionList(newRecentlyUsedRegionList);
    pageLoading && setPageLoading(false);
    let snapshotDataChanged = false;

    // checking to see if snapshots data has changed
    //  effects the memoized values for the table component which depend on of the snapshot state. If the snapshot state changes the Table component is rerendered
    if (newArchSnapshotSummary.snapshots.length !== snapshots.length) {
      snapshotDataChanged = true;
    } else {
      let map1 = new Map([["in_progress", 0], ["complete", 0], ["failed", 0]]);
      snapshots.forEach(el => map1.set(el.generation_state, map1.get(el.generation_state) + 1));
      let map2 = new Map([["in_progress", 0], ["complete", 0], ["failed", 0]]);
      newArchSnapshotSummary.snapshots.forEach(el => map2.set(el.generation_state, map2.get(el.generation_state) + 1));
      if (map1.get("in_progress") !== map2.get("in_progress") || map1.get("complete") !== map2.get("complete") || map1.get("failed") !== map2.get("failed")) {
        snapshotDataChanged = true;
      }
    }
    snapshotDataChanged && setSnapshots(newArchSnapshotSummary.snapshots);

    // check if any generation states are "in_progress"
    // resulting in setting IsSnapshotGenerationInProgress to true or false which effects the setInterval and recalling this function

    let snapshotInProgress = newArchSnapshotSummary.snapshots.some(el => el.generation_state === "in_progress");
    isSnapshotGenerationInProgress && !snapshotInProgress && setIsSnapshotGenerationInProgress(false);
    !isSnapshotGenerationInProgress && snapshotInProgress && setIsSnapshotGenerationInProgress(true);
  };
  const handleLocalDatetime = (data, type) => {
    const dt = moment.utc(data);
    const time = dt.local().format("h:mm A");
    const date = dt.local().format("M/D/YYYY");
    if (type === "date") {
      return date;
    } else if (type === "date-time-1") {
      return `${date}`;
    } else if (type === "date-time-2") {
      return `${date} ${time}`;
    }
  };
  const handleTemplateConfirm = async () => {
    removeAlert();
    setIsTemplateConfirmLoading(true);
    const confirmAccessRes = await postArchSnapshotConfirmAccess();
    if (confirmAccessRes?.is_error) {
      setIsTemplateConfirmLoading(false);
      createAlert("error", "Error while confirming the template. Please check your template and try again");
      return;
    }
    setIsTemplateCreated(true);
    await handleGetArchSnapshotSummary();
    setIsTemplateConfirmLoading(false);
    return;
  };
  const handleSnapshotGeneration = async e => {
    e.preventDefault();
    removeAlert();

    // this is horrible code done quickly, please improve later
    const selectedRegions = createSnapshotFormValues.regions;
    if (!selectedRegions[0]) {
      createAlert("error", "Please select 1 or more regions");
      return;
    }
    setIsSnapshotGenerationInitButtonDisabled(true);
    setIsSnapshotGenerationInitInProgress(true);
    const generateSnapshotScope = selectedRegions.map(region => {
      return {
        scope_type: "aws_region",
        scope_value: region
      };
    });
    const generateRes = await postArchSnapshotGenerate({
      scope: generateSnapshotScope
    });
    if (generateRes?.is_error) {
      createAlert("error", "Error while generating snapshot. Please try again soon");
      setIsSnapshotGenerationInitButtonDisabled(false);
      setIsSnapshotGenerationInitInProgress(false);
      return;
    }
    await handleGetArchSnapshotSummary();

    //resetting all regions dropdown and previously selecting regions checkboxes
    resetCreateSnapshotFormValues();
    setIsSnapshotGenerationInitButtonDisabled(false);
    setIsSnapshotGenerationInitInProgress(false);
  };

  // ui hooks
  const [isCreateSnapshotFormValid, createSnapshotFormValidationProps, onCreateSnapshotFieldValidationChange] = useFormValidation(handleSnapshotGeneration);
  if (pageLoading) {
    return /*#__PURE__*/React.createElement(PageLoader, null);
  }
  if (cloudType !== "aws") {
    return /*#__PURE__*/React.createElement(LockedPage, {
      backgroundImage: archSnapshotLockedBackground
    }, /*#__PURE__*/React.createElement(LockedPageModal, {
      title: "Architecture snapshots are only available for AWS cloud accounts",
      description: /*#__PURE__*/React.createElement(React.Fragment, null, "Please select your AWS account or", typeof route_cloud_account_setup === "string" ? /*#__PURE__*/React.createElement(Link, {
        LinkComponent: ReactRouterLink,
        href: route_cloud_account_setup
      }, "create a new one") : "create a new one")
    }));
  }
  const templateCreationForm = /*#__PURE__*/React.createElement(Section, {
    title: archSnapshotSummary?.message ? archSnapshotSummary.message : null
  }, /*#__PURE__*/React.createElement("div", {
    className: "arch-snapshot-template-creation-form"
  }, /*#__PURE__*/React.createElement("a", {
    href: archSnapshotSummary?.accessTemplate?.url,
    target: "_blank"
  }, /*#__PURE__*/React.createElement("img", {
    alt: "",
    src: cloudFormationLaunchStackImage
  })), /*#__PURE__*/React.createElement(Button, {
    onClick: handleTemplateConfirm,
    styleType: "submit",
    loading: isTemplateConfirmLoading
  }, "Confirm")));
  return /*#__PURE__*/React.createElement("div", {
    id: "architecture_snapshots"
  }, isTemplateCreated === false ? /*#__PURE__*/React.createElement(Widget, {
    title: "Create Snapshots"
  }, /*#__PURE__*/React.createElement(Alert, null), templateCreationForm) : /*#__PURE__*/React.createElement(Widget, {
    title: "Create Snapshots"
  }, /*#__PURE__*/React.createElement(Section, {
    title: "Please Select Regions"
  }, /*#__PURE__*/React.createElement(Alert, null), /*#__PURE__*/React.createElement(FormValidation, createSnapshotFormValidationProps, archSnapshotSummary?.next_available_generation_date >= new Date().toISOString().slice(0, 10) && /*#__PURE__*/React.createElement("p", null, "On ", handleLocalDatetime(archSnapshotSummary?.next_available_generation_date, "date-time-1"), " you can regenerate snapshots for all previoulsy generated regions."), recentlyUsedRegionList.length > 0 ? /*#__PURE__*/React.createElement("div", {
    className: "arch-snapshot-previously-selected-region-list-wrapper"
  }, /*#__PURE__*/React.createElement("p", null, "Previously selected regions"), /*#__PURE__*/React.createElement("div", {
    className: "arch-snapshot-previously-selected-region-list"
  }, recentlyUsedRegionList.map((region, key) => {
    const regionName = region.scope_value;
    return /*#__PURE__*/React.createElement(ControlledValidationCheckbox, {
      key: key,
      name: regionName,
      checked: createSnapshotFormValues.regions.includes(regionName) || false,
      onChange: ({
        name,
        value
      }) => {
        let newRegions = [...createSnapshotFormValues.regions];
        if (newRegions.includes(regionName) && value === false) {
          newRegions = newRegions.filter(region => region !== regionName);
        }
        if (newRegions.includes(regionName) === false && value === true) {
          newRegions.push(regionName);
        }
        onCreateSnapshotFieldChange({
          name: "regions",
          value: newRegions
        });
      },
      disabled: isSnapshotGenerationInitButtonDisabled || region.is_gen_restricted,
      requireChecked: false,
      onValidationChange: () => onCreateSnapshotFieldValidationChange({
        name: regionName,
        isValid: true
      })
    }, regionName);
  }))) : null, /*#__PURE__*/React.createElement(ControlledValidationSelect, {
    className: "arch-snapshot-region-select",
    name: "regions",
    options: regionList.map(region => {
      return {
        label: region.scope_value,
        value: region.scope_value,
        disabled: region.is_gen_restricted
      };
    }),
    value: createSnapshotFormValues.regions,
    onChange: onCreateSnapshotFieldChange,
    onValidationChange: () => onCreateSnapshotFieldValidationChange({
      name: "regions",
      isValid: true
    }),
    required: false,
    label: "All Regions",
    placeholder: "Region",
    disabled: isSnapshotGenerationInitButtonDisabled,
    showToggleIcon: true,
    multiple: true
  }), /*#__PURE__*/React.createElement(Button, {
    className: "arch-snapshot-generate-button",
    loading: isSnapshotGenerationInitInProgress,
    type: "submit",
    styleType: "submit",
    disabled: isSnapshotGenerationInitButtonDisabled || createSnapshotFormValues.regions.length === 0
  }, "Initialize Snapshot Generation"))), templateCreationForm), /*#__PURE__*/React.createElement(Widget, {
    title: "Architecture Snapshots"
  }, /*#__PURE__*/React.createElement(TableSortWrapper, {
    columns: memoizedSnapshotCols || [],
    rows: memoizedSnapshotRows || [],
    noDataMessage: "There are currently no snapshots.",
    defaultSort: {
      dataKey: "generation_state_dt",
      order: "DESC"
    }
  }, /*#__PURE__*/React.createElement(Table, null, /*#__PURE__*/React.createElement(TableHeader, null), /*#__PURE__*/React.createElement(TableBodyNoDataWrapper, null, /*#__PURE__*/React.createElement(TableBody, null))))));
};
export { ArchSnapshot };